import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Hero from "../../components/Hero/Hero";
import toast from "../../components/ui/toast";
import PageFooter from "../../components/PageFooter/PageFooter";
import InfoStats from "../Audit/InfoStats";
import ModalMoreInfo from "./ModalMoreInfoSL";
import StoreLocatorScoring from "../../components/StoreLocatorScoring/StoreLocatorScoring";
import ReCAPTCHA from "react-google-recaptcha";
import Seo from "../../i18n/Seo";
import useLang from "../../i18n/useLang";
import content from "./content";
import "./AuditStoreLocator.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/tech";

// Hard cap on the round-trip. The backend deadline is ~95s (scraper + Mistral),
// so 100s leaves a small margin for network. Anything longer means the proxy in
// front of the API likely killed the connection — fail fast with a useful UX
// instead of an infinite spinner.
const REQUEST_TIMEOUT_MS = 100000;

// When each loading step starts (ms after submit). Labels are in content.jsx.
const LOADING_STEP_DELAYS = [0, 5000, 10000, 16000, 22000, 30000];

const isValidUrl = (string) => {
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const AuditStoreLocator = () => {
  const lang = useLang();
  const t = content[lang];
  const loadingSteps = t.loader.steps;
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [result, setResult] = useState(null);
  const [displayResults, setDisplayResults] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isSA, setIsSA] = useState(false);
  const recaptchaRef = React.useRef();
  const stepTimersRef = useRef([]);
  const location = useLocation();

  useEffect(() => {
    if (location.search.includes("access=localranker")) {
      setIsSA(true);
    }
  }, [location.search]);

  const handleOpenModal = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      toast.warning(t.toasts.emptyUrl);
      return;
    }
    if (!isValidUrl(trimmed)) {
      toast.warning(t.toasts.invalidUrl);
      return;
    }

    if (isSA) {
      submitAudit(trimmed);
    } else {
      setModalOpen(true);
    }
  };

  const submitAudit = async (auditUrl, businessInfo) => {
    setModalOpen(false);
    setDisplayResults(false);
    setResult(null);

    const tokenCaptcha = isSA ? true : await recaptchaRef.current?.executeAsync();
    if (!tokenCaptcha) {
      toast.warning(t.toasts.captcha);
      return;
    }

    setIsLoading(true);
    setActiveStep(0);
    setAuditError(null);
    setDisplayResults(false);
    setResult(null);

    stepTimersRef.current = LOADING_STEP_DELAYS.map((delay, i) =>
      setTimeout(() => setActiveStep(i), delay),
    );

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const params = new URLSearchParams({ url: auditUrl, lang });
      if (businessInfo) {
        params.append("businessInfo", JSON.stringify(businessInfo));
      } else if (isSA) {
        params.append("sa", "true");
      }

      const response = await fetch(
        `${API_URL}/store-locator-audit?${params.toString()}`,
        { method: "POST", signal: controller.signal },
      );

      if (response.status === 429) {
        setAuditError(t.errors.tooManyRequests);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        setAuditError(t.errors.invalidResponse);
        return;
      }

      if (!response.ok) {
        setAuditError(data.error || t.errors.generic);
        return;
      }

      setResult(data);
      setDisplayResults(true);
    } catch (e) {
      console.error(e);
      if (e.name === "AbortError") {
        setAuditError(t.errors.timeout);
      } else {
        setAuditError(t.errors.network);
      }
    } finally {
      clearTimeout(timeoutId);
      stepTimersRef.current.forEach(clearTimeout);
      stepTimersRef.current = [];
      setIsLoading(false);
      setActiveStep(0);
      recaptchaRef.current?.reset();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleOpenModal();
    }
  };

  return (
    <div>
      <Seo
        routeId="storeLocatorAudit"
        title={t.seo.title}
        description={t.seo.description}
        ogTitle={t.seo.ogTitle}
        ogDescription={t.seo.ogDescription}
      />
      <Hero
        title={t.hero.title}
        subtitle={t.hero.subtitle}
        ctaText={t.hero.cta}
        ctaOnClick={() =>
          document
            .querySelector(".sl-search-form")
            ?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      />

      <div className="sl-search-form">
        <div className="search-form__card">
          <div className="search-form__row">
            <div className="search-form__input-wrap">
              <input
                type="url"
                placeholder={t.form.placeholder}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>
            <button
              className="search-form__btn"
              type="button"
              onClick={handleOpenModal}
              disabled={isLoading}
            >
              {isLoading ? t.form.submitting : t.form.submit}
            </button>
          </div>
        </div>
      </div>

      {isLoading && (
        <AuditLoader
          url={url}
          title={t.loader.title}
          steps={loadingSteps}
          activeStep={activeStep}
        />
      )}

      <ReCAPTCHA
        sitekey="6LcLC9UZAAAAAOY_vgaU__i3Qhs1tmHtvbibjD2V"
        size="invisible"
        ref={recaptchaRef}
      />

      {!isLoading && auditError && (
        <div className="sla-error">
          <div className="sla-error__icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <p className="sla-error__msg">{auditError}</p>
          <button className="sla-error__retry" type="button" onClick={() => { setAuditError(null); handleOpenModal(); }}>
            {t.errors.retry}
          </button>
        </div>
      )}

      {!isLoading && !displayResults && !auditError && <InfoStats />}

      {displayResults && result && (
        <StoreLocatorScoring result={result} hasFullAccess={isSA} />
      )}

      <ModalMoreInfo
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={(data) => submitAudit(url.trim(), data)}
        title={t.modal.title}
        submitLabel={t.modal.submit}
      />

      <section className="sl-seo">
        <div className="sl-seo__intro">
          <h2>{t.seoSection.introTitle}</h2>
          <p>{t.seoSection.intro}</p>
        </div>

        <div className="sl-seo__grid">
          {t.seoSection.cards.map((card) => (
            <div key={card.title} className="sl-seo__card">
              <span className="sl-seo__card-icon">{card.icon}</span>
              <h3>{card.title}</h3>
              <p>{card.text}</p>
            </div>
          ))}
        </div>

        {t.seoSection.blocks.map((block) => (
          <div key={block.title} className="sl-seo__block">
            <h3>{block.title}</h3>
            <p>{block.text}</p>
          </div>
        ))}

        <div className="sl-seo__faq">
          <h3>{t.seoSection.faqTitle}</h3>
          {t.seoSection.faq.map((item) => (
            <div key={item.q} className="sl-seo__faq-item">
              <h4>{item.q}</h4>
              <p>{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <PageFooter />
    </div>
  );
};

const AuditLoader = ({ url, title, steps, activeStep }) => {
  const progress = ((activeStep + 1) / steps.length) * 100;

  return (
    <div className="audit-loader">
      <div className="audit-loader__card">
        <div className="audit-loader__header">
          <div className="audit-loader__pulse" />
          <div>
            <p className="audit-loader__title">{title}</p>
            <p className="audit-loader__url">{url}</p>
          </div>
        </div>

        <div className="audit-loader__bar-track">
          <div
            className="audit-loader__bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="audit-loader__steps">
          {steps.map((step, i) => {
            const done = i < activeStep;
            const active = i === activeStep;
            return (
              <div
                key={i}
                className={`audit-loader__step${done ? " audit-loader__step--done" : ""}${active ? " audit-loader__step--active" : ""}`}
              >
                <span className="audit-loader__step-dot">
                  {done ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2.5 6L5 8.5L9.5 3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <span className="audit-loader__step-num">{i + 1}</span>
                  )}
                </span>
                <div className="audit-loader__step-text">
                  <span className="audit-loader__step-label">{step.label}</span>
                  {active && (
                    <span className="audit-loader__step-detail">
                      {step.detail}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AuditStoreLocator;
